import React, { useMemo } from 'react';
import {
    Box, Card, CardContent, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tooltip, Typography,
} from '@mui/material';
import { ACTIVITY_STATUS_COLORS, ExecutionOverview } from '../../../types/execution';
import { GanttBar, GanttHeader } from './GanttBar';
import { formatPct, timelineFromOverview } from './executionUtils';

interface ExecutiveViewProps {
    overview: ExecutionOverview;
}

const Kpi: React.FC<{ label: string; value: string | number; color?: string }> = ({ label, value, color }) => (
    <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="h4" sx={{ color }}>{value}</Typography>
        </CardContent>
    </Card>
);

const ExecutiveView: React.FC<ExecutiveViewProps> = ({ overview }) => {
    const range = useMemo(() => timelineFromOverview(overview), [overview]);
    const { phase_names: phaseNames, rows } = overview.executive_view;
    const openRisks = overview.risks.filter((r) => r.status === 'OPEN');
    const openIssues = overview.issues.filter((r) => r.status === 'OPEN');
    const highOpen = [...openRisks, ...openIssues].filter((r) => r.criticality === 'HIGH').length;
    const delayed = rows.reduce((acc, row) =>
        acc + Object.values(row.phases).filter((cell) => cell.status === 'DELAYED' || cell.status === 'CRITICAL' || cell.status === 'BLOCKED').length, 0);

    return (
        <Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}><Kpi label="Frentes" value={rows.length} /></Grid>
                <Grid item xs={6} md={3}><Kpi label="Fases com atraso" value={delayed} color={delayed ? ACTIVITY_STATUS_COLORS.DELAYED : undefined} /></Grid>
                <Grid item xs={6} md={3}>
                    <Kpi label="Riscos / incidências abertos" value={`${openRisks.length} / ${openIssues.length}`} />
                </Grid>
                <Grid item xs={6} md={3}><Kpi label="Criticidade alta abertos" value={highOpen} color={highOpen ? ACTIVITY_STATUS_COLORS.CRITICAL : undefined} /></Grid>
            </Grid>

            {rows.length === 0 ? (
                <Typography color="text.secondary">
                    Nenhuma frente cadastrada. Crie as frentes e o cronograma na aba "Cronograma".
                </Typography>
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Frente</TableCell>
                                <TableCell>Owner</TableCell>
                                {phaseNames.map((name) => (
                                    <TableCell key={name} align="center">{name}</TableCell>
                                ))}
                                <TableCell sx={{ minWidth: 280 }}><GanttHeader range={range} /></TableCell>
                                <TableCell align="center">Riscos abertos</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, index) => {
                                const ws = overview.workstreams.find((w) => w.id === row.workstream_id);
                                return (
                                    <TableRow key={row.workstream_id}>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            {String(index + 1).padStart(2, '0')}. {row.name}
                                        </TableCell>
                                        <TableCell>{row.owner_name || '-'}</TableCell>
                                        {phaseNames.map((name) => {
                                            const cell = row.phases[name];
                                            if (!cell || (!cell.planned_start && cell.progress_real === 0)) {
                                                return <TableCell key={name} align="center">-</TableCell>;
                                            }
                                            return (
                                                <TableCell key={name} align="center">
                                                    <Tooltip title={`Esperado: ${formatPct(cell.progress_expected)}`}>
                                                        <Box
                                                            sx={{
                                                                display: 'inline-block', px: 1, py: 0.25, borderRadius: 1, color: '#fff',
                                                                fontWeight: 600, bgcolor: ACTIVITY_STATUS_COLORS[cell.status], minWidth: 48,
                                                            }}
                                                        >
                                                            {formatPct(cell.progress_real)}
                                                        </Box>
                                                    </Tooltip>
                                                </TableCell>
                                            );
                                        })}
                                        <TableCell>
                                            {row.status_note ? (
                                                <Typography variant="caption" color="text.secondary">{row.status_note}</Typography>
                                            ) : ws ? (
                                                <Box display="flex" flexDirection="column" gap={0.75}>
                                                    {ws.phases.filter((phase) => phase.planned_start).map((phase) => (
                                                        <GanttBar
                                                            key={phase.id}
                                                            range={range}
                                                            plannedStart={phase.planned_start}
                                                            plannedEnd={phase.planned_end}
                                                            progress={phase.progress_real}
                                                            status={phase.status}
                                                            referenceDate={overview.report_date}
                                                            label={phase.milestone_label}
                                                        />
                                                    ))}
                                                </Box>
                                            ) : null}
                                        </TableCell>
                                        <TableCell align="center">{row.open_risks || '-'}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default ExecutiveView;
